import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea
} from 'recharts';
import { TICK_STYLE, TOOLTIP_WRAPPER_STYLE } from './constants';
import { CostTooltip } from './tooltips';
import type { CostDataPoint, ChartReferenceArea } from './types';

const CostBarLabel: React.FC<unknown> = ({ x, y, width, value }) => {
  if (!value || value <= 0 || !Number.isFinite(value)) return null;
  const label = value >= 1 ? `$${value.toFixed(1)}` : `$${value.toFixed(2)}`;
  return (
    <text x={x + width / 2} y={y - 4} textAnchor="middle" fontSize={12} fontWeight="bold" fill="#d1d5db">
      {label}
    </text>
  );
};

interface CostTrendChartProps {
  data: CostDataPoint[];
  showOverlays: boolean;
  referenceAreas: ChartReferenceArea[];
  showMovingAverage: boolean;
  formatTimestamp: (value: number | string) => string;
}

export const CostTrendChart: React.FC<CostTrendChartProps> = ({
  data,
  showOverlays,
  referenceAreas,
  showMovingAverage,
  formatTimestamp
}) => (
  <div className="p-2 sm:p-3" style={{ minHeight: '240px' }}>
    <ResponsiveContainer width="100%" height={220}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        {showOverlays && referenceAreas.map((area) => (
          <ReferenceArea
            key={`${area.type}-${area.start}`}
            x1={area.start}
            x2={area.end}
            fill={area.type === 'holiday' ? '#fbbf24' : '#9ca3af'}
            fillOpacity={0.2}
            stroke="none"
          />
        ))}
        <XAxis
          dataKey="timestamp"
          tick={TICK_STYLE}
          tickFormatter={formatTimestamp}
          stroke="#6b7280"
          interval="preserveEnd"
        />
        <YAxis
          tick={TICK_STYLE}
          stroke="#6b7280"
        />
        <Tooltip
          content={<CostTooltip />}
          wrapperStyle={TOOLTIP_WRAPPER_STYLE}
        />
        <Legend />
        <Bar
          dataKey="cost"
          fill="#10b981"
          name="Cost"
          radius={[2, 2, 0, 0]}
          label={<CostBarLabel />}
        />
        {showMovingAverage && (
          <Line
            type="monotone"
            dataKey="totalCostMA14"
            stroke="#2563eb"
            strokeWidth={2}
            name="Cost (14-day MA)"
            dot={false}
            connectNulls
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);
